import { kebabCase } from 'lodash';
import pluralize from 'pluralize';
import React, { RefObject } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { DisplayButton } from '@xo-union/tk-ui-links';
import { PagesFragment } from '../../generated/graphql';
import GuidanceItemCard from '../PageGuidance/GuidanceItemCard';
import { PageItemIcon } from '../PageItemsRail/PageItemIcon';
import { Editorial } from '@xo-union/tk-ui-typography';
import useComingSoonPage from '../../helpers/useComingSoonPage';
import { previewPageRoute } from '../../helpers/routes';
import { getTargetOrigin } from '../../helpers/getTargetOrigin';
import styles from './addPageRail.scss';
import PhotoItemField from '../PageItemsRail/PhotoItemField';
import GifItemField from '../PageItemsRail/GifItemField';
import useIsLiteSite from '../../helpers/useIsLiteSite';

interface Props {
  page: PagesFragment;
  iframeRef: RefObject<HTMLIFrameElement>;
  onClick: () => void;
}

export const AddPageContentRail: React.FC<Props> = ({ page, onClick, iframeRef }) => {
  const addComingSoon = useComingSoonPage(page);
  const history = useHistory();
  const liteSite = useIsLiteSite();

  if (!page) {
    return null;
  }

  const firstTwoPageGuidanceItems = page?.pageItemGuidances?.slice(0, 2) || [];
  const rest = page?.pageItemGuidances?.slice(2) || [];
  const pageItemTypes = rest
    .map(item => item.type || '')
    .filter(type => {
      return !!type;
    });

  const changePage = () => {
    if (iframeRef?.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(
        JSON.stringify({
          type: 'currentPage',
          data:
            typeof page === 'string'
              ? {
                  // GV will 404 if RSVP is hidden unless preview is passed
                  routeName: 'rsvp?preview=true',
                }
              : page,
        }),
        getTargetOrigin()
      );
    }
  };

  const comingSoonHandler = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    await addComingSoon(event);

    if (page.routeName) {
      changePage();
      history.push(previewPageRoute(page.routeName));
    }
    // close AddPageRail
    onClick();
  };

  return (
    <div className={styles.addContentContainer}>
      <Editorial className={styles.subtitle} bold>
        Get Started With a Template:
      </Editorial>
      <div style={{ display: 'flex' }}>
        {firstTwoPageGuidanceItems.map((guidance, index) => {
          return <GuidanceItemCard key={index} pageId={page.id} guidance={guidance} preview />;
        })}
      </div>
      <Editorial className={styles.subtitle} bold>
        Or add any of these page items:
      </Editorial>
      <div className={styles.itemContainer}>
        {pageItemTypes.map(pageItemType => {
          if (pageItemType === 'PhotoItem') {
            return <PhotoItemField key={`PhotoItem-${page.id}`} pageId={page.id} />;
          } else if (pageItemType === 'GifItem') {
            return <GifItemField key={`GifItem-${page.id}`} pageId={page.id} />;
          } else {
            return (
              <Link key={pageItemType} to={`/preview/pages/${page.id}/${pluralize(kebabCase(pageItemType))}`}>
                <PageItemIcon icon={pageItemType} />
              </Link>
            );
          }
        })}
      </div>
      {!!page.items && liteSite && (
        <div className={styles.addComingSoonContainer}>
          Not sure what to add yet?{' '}
          <DisplayButton
            data-testid="cardheader-subhead-action"
            onClick={comingSoonHandler}
            className={styles.addComingSoon}
          >
            Add with "coming soon"
          </DisplayButton>
        </div>
      )}
    </div>
  );
};

export default AddPageContentRail;
